/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2026-04-29
 * Description: Public registration form. Anyone can submit; an admin must
 *              approve in /admin/api-access-requests before access is granted.
 */

import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '@services/api'
import { toast } from 'react-toastify'

const ApiAccessRegister: React.FC = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState({
    email: params.get('email') ?? '',
    first_name: '',
    last_name: '',
    company: '',
    use_case: '',
    request_type: 'documentation' as 'documentation' | 'access',
  })

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await api.accessRequestRegister(form)
      navigate(`/api-access/registered?email=${encodeURIComponent(form.email)}`)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to submit request'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-xl py-12">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Request DAL-ify External API Access</h1>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Submit this form to request access to the DAL-ify external API documentation (Sonata + TMF). An admin will
        review and email you a one-time code.
      </p>

      <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="First name" name="first_name" value={form.first_name} onChange={onChange} required />
          <Field label="Last name" name="last_name" value={form.last_name} onChange={onChange} required />
        </div>
        <Field label="Work email" name="email" type="email" value={form.email} onChange={onChange} required />
        <Field label="Company" name="company" value={form.company} onChange={onChange} required />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">What kind of access?</label>
          <div className="mt-2 space-y-2">
            <label className="flex cursor-pointer items-start gap-3 rounded border border-gray-200 p-3 hover:border-indigo-300 dark:border-gray-700">
              <input
                type="radio"
                name="request_type"
                value="documentation"
                checked={form.request_type === 'documentation'}
                onChange={onChange}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">API Documentation</span>
                <span className="block text-xs text-gray-600 dark:text-gray-400">
                  View Sonata + TMF Swagger UI at /api/docs/external
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded border border-gray-200 p-3 hover:border-indigo-300 dark:border-gray-700">
              <input
                type="radio"
                name="request_type"
                value="access"
                checked={form.request_type === 'access'}
                onChange={onChange}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Live API Access</span>
                <span className="block text-xs text-gray-600 dark:text-gray-400">
                  Programmatic integration / API key issuance (issued after admin review)
                </span>
              </span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Use case (optional)</label>
          <textarea
            name="use_case"
            value={form.use_case}
            onChange={onChange}
            rows={4}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:border-gray-600 dark:shadow-none"
            placeholder={
              form.request_type === 'access'
                ? 'What systems will integrate, expected volume, etc.'
                : 'What are you planning to integrate?'
            }
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 dark:shadow-none"
        >
          {submitting ? 'Submitting…' : 'Submit request'}
        </button>
      </form>

      <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
        Already verified?{' '}
        <Link className="text-indigo-600 underline" to="/api-access/login">
          Sign in with a one-time code
        </Link>
        .
      </p>
    </div>
  )
}

interface FieldProps {
  label: string
  name: string
  value: string
  type?: string
  required?: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const Field: React.FC<FieldProps> = ({ label, name, value, type = 'text', required, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:border-gray-600 dark:shadow-none"
    />
  </div>
)

export default ApiAccessRegister
